import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Pressable from '@atlaskit/primitives/pressable';
import { AuthError } from '@atlaskit/outbound-auth-flow-client/error';
import { auth } from '@atlaskit/outbound-auth-flow-client/auth';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';

import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { LoadableAfterPaint } from '@confluence/loadable';
import { usePageSpaceKey } from '@confluence/page-context';

import { CloseMenu } from './CloseMenu.tsx';
import { InternalTestingLozenge } from './InternalTestingLozenge.tsx';
import { useDismissCuration } from './hooks/useDismissCuration.ts';
import { StackedContentIcons } from './StackedContentIcons.tsx';
import {
	AUTH_OPTIONS,
	EXTERNAL_PRODUCT_CONNECTION,
	type AuthenticationStatus,
	type ExternalProductConnection,
} from './__types__/RemoteObject.ts';

const ThirdPartyAuthenticationModal = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ThirdPartyAuthenticationModal" */ './ThirdPartyAuthenticationModal.tsx'
			)
		).ThirdPartyAuthenticationModal,
});

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.small'),
		marginTop: token('space.100'),
		overflow: 'hidden',
		padding: token('space.150'),
		position: 'relative',
	},
	topRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		marginBottom: token('space.050'),
	},
	mainAction: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.050'),
		backgroundColor: 'transparent',
		padding: token('space.0'),
		width: '100%',
		textAlign: 'start',
	},
});

const i18n = defineMessages({
	title: {
		id: 'space-curation.space-navigation.third-party-auth.title',
		defaultMessage: 'Make the most of docs, sheets, slides, and folders',
		description:
			'Title for the third-party authentication entry point card, specifically about google drive',
	},
	titleGoogleDrive: {
		id: 'space-curation.space-navigation.third-party-auth.title-google-drive',
		defaultMessage: 'Connect to Google Drive to bring content into your space.',
		description:
			'Body text for the third-party auth entry point card when only Google Drive is unconnected',
	},
	titleMicrosoft: {
		id: 'space-curation.space-navigation.third-party-auth.title-microsoft',
		defaultMessage: 'Connect to Microsoft to bring content into your space.',
		description:
			'Body text for the third-party auth entry point card when only Microsoft is unconnected',
	},
	titleMulti: {
		id: 'space-curation.space-navigation.third-party-auth.title-multi',
		defaultMessage: 'Connect to apps to bring content into your space.',
		description:
			'Body text for the third-party auth entry point card when multiple apps are unconnected',
	},
	connect: {
		id: 'space-curation.space-navigation.third-party-auth.connect-apps',
		defaultMessage: 'Connect apps',
		description:
			'Label for the connect link in the third-party auth entry point card. It opens a modal where users can choose an app to connect',
	},
	connectProvider: {
		id: 'space-curation.space-navigation.third-party-auth.connect-provider',
		defaultMessage: 'Connect {providerName}',
		description:
			'Label for the connect link when there is only one unconnected app, directly initiating auth for that specific provider',
	},
	successFlagTitle: {
		id: 'space-curation.space-navigation.third-party-auth.success-title-v2',
		defaultMessage: '{providerName} connected',
		description:
			'Title for the success flag shown after a user successfully authenticates with a third-party provider',
	},
	successFlagDescription: {
		id: 'space-curation.space-navigation.third-party-auth.success-description',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "We're on it! Rovo is looking for content for this space, check back later.",
		description:
			'Description for the success flag shown after a user successfully authenticates with a third-party provider',
	},
	cancelledFlagTitle: {
		id: 'space-curation.space-navigation.third-party-auth.cancelled-title',
		defaultMessage: 'Connection cancelled',
		description:
			'Title for the warning flag shown when the user closes the auth popup without completing authentication',
	},
	cancelledFlagDescription: {
		id: 'space-curation.space-navigation.third-party-auth.cancelled-description-v2',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "Couldn't connect {providerName}. Please try again.",
		description:
			'Description for the warning flag shown when the user closes the auth popup without completing authentication',
	},
	errorFlagTitle: {
		id: 'space-curation.space-navigation.third-party-auth.error-title',
		defaultMessage: 'Connection failed',
		description:
			'Title for the error flag shown when third-party authentication fails unexpectedly',
	},
	errorFlagDescription: {
		id: 'space-curation.space-navigation.third-party-auth.error-description-v2',
		defaultMessage: 'Something went wrong connecting {providerName}. Please try again.',
		description:
			'Description for the error flag shown when third-party authentication fails unexpectedly',
	},
	remindTomorrowSuccessTitle: {
		id: 'space-curation.space-navigation.third-party-auth.remind-tomorrow.success-title',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "Got it, we'll check back later",
		description:
			'Title of the success flag shown after the user chooses to be reminded about the connect apps suggestion tomorrow',
	},
	remindTomorrowSuccessDescription: {
		id: 'space-curation.space-navigation.third-party-auth.remind-tomorrow.success-description',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "We'll remind you about these suggestions again in 24 hours.",
		description:
			'Description of the success flag shown after the user chooses to be reminded about the connect apps suggestion tomorrow',
	},
	neverShowAgainSuccessTitle: {
		id: 'space-curation.space-navigation.third-party-auth.never-show-again.success-title',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "We won't show this again",
		description:
			'Title of the success flag shown after the user chooses to never see the connect apps suggestion in this space again',
	},
	neverShowAgainSuccessDescription: {
		id: 'space-curation.space-navigation.third-party-auth.never-show-again.success-description',
		defaultMessage: 'You will no longer see curation suggestions in this space.',
		description:
			'Description of the success flag shown after the user chooses to never see the connect apps suggestion in this space again',
	},
	dismissErrorTitle: {
		id: 'space-curation.space-navigation.third-party-auth.dismiss.error-title',
		defaultMessage: 'Something went wrong',
		description: 'Title of the error flag shown when dismissing the connect apps suggestion fails',
	},
	dismissErrorDescription: {
		id: 'space-curation.space-navigation.third-party-auth.dismiss.error-description',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: "We couldn't update your preference. Please try again.",
		description:
			'Description of the error flag shown when dismissing the connect apps suggestion fails',
	},
});

type ThirdPartyAuthenticationProps = {
	unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	onThirdPartyAuthRefetch: () => void | Promise<void>;
	onAuthSuccess?: (product: ExternalProductConnection) => void;
	spaceId?: string;
	mountTimestamp: number;
};

export const ThirdPartyAuthentication = ({
	unauthenticatedServices,
	onThirdPartyAuthRefetch,
	onAuthSuccess,
	spaceId,
	mountTimestamp,
}: ThirdPartyAuthenticationProps): React.JSX.Element | null => {
	const intl = useIntl();
	const [isVisible, setIsVisible] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const hasFiredRenderedRef = useRef(false);
	const [spaceKey] = usePageSpaceKey();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { dismissAction } = useDismissCuration();

	const eligibleConnectors = useMemo(
		() =>
			AUTH_OPTIONS.filter(
				(option) =>
					unauthenticatedServices[option] &&
					Boolean(unauthenticatedServices[option]?.outboundAuthUrl),
			),
		[unauthenticatedServices],
	);

	useEffect(() => {
		if (isVisible && spaceId && !hasFiredRenderedRef.current) {
			hasFiredRenderedRef.current = true;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'rendered',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						spaceKey,
						entryPoint: 'authEntry',
						suggestionCount: 0,
						eligibleConnectors,
						renderLatencyMs: Date.now() - mountTimestamp,
					},
				},
			}).fire();
		}
	}, [isVisible, spaceId, spaceKey, eligibleConnectors, createAnalyticsEvent, mountTimestamp]);

	const handleDismiss = async (flags: FlagsStateContainer) => {
		if (spaceId) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'dismissed',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						spaceKey,
						entryPoint: 'authEntry',
						dismissMethod: 'close_button',
					},
				},
			}).fire();
		}
		try {
			await dismissAction({ remindTomorrow: true });
			setIsVisible(false);
			void flags?.showSuccessFlag({
				id: 'space-curation.space-navigation.third-party-auth.remind-tomorrow.success',
				title: intl.formatMessage(i18n.remindTomorrowSuccessTitle),
				description: intl.formatMessage(i18n.remindTomorrowSuccessDescription),
				isAutoDismiss: true,
			});
		} catch {
			// If the dismiss POST fails, keep the card visible so the
			// preference is not silently lost.
			void flags?.showErrorFlag({
				id: 'space-curation.space-navigation.third-party-auth.remind-tomorrow.error',
				title: intl.formatMessage(i18n.dismissErrorTitle),
				description: intl.formatMessage(i18n.dismissErrorDescription),
				isAutoDismiss: true,
			});
		}
	};

	const handleNeverShowAgain = async (flags: FlagsStateContainer) => {
		if (spaceId) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'dismissed',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						spaceKey,
						entryPoint: 'authEntry',
						dismissMethod: 'never_show_again',
					},
				},
			}).fire();
		}
		try {
			await dismissAction({ neverShowAgain: true });
			setIsVisible(false);
			void flags?.showSuccessFlag({
				id: 'space-curation.space-navigation.third-party-auth.never-show-again.success',
				title: intl.formatMessage(i18n.neverShowAgainSuccessTitle),
				description: intl.formatMessage(i18n.neverShowAgainSuccessDescription),
				isAutoDismiss: true,
			});
		} catch {
			// If the never-show-again POST fails, keep the card visible so
			// the preference is not silently lost.
			void flags?.showErrorFlag({
				id: 'space-curation.space-navigation.third-party-auth.never-show-again.error',
				title: intl.formatMessage(i18n.dismissErrorTitle),
				description: intl.formatMessage(i18n.dismissErrorDescription),
				isAutoDismiss: true,
			});
		}
	};

	if (!isVisible || eligibleConnectors.length === 0) {
		return null;
	}

	const getProviderName = (option: (typeof AUTH_OPTIONS)[number]): string => {
		switch (option) {
			case EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE:
				return 'Google Drive';
			case EXTERNAL_PRODUCT_CONNECTION.MICROSOFT:
				return 'Microsoft';
			default:
				return option;
		}
	};

	const handleConnect = async (
		option: (typeof AUTH_OPTIONS)[number],
		flags: FlagsStateContainer,
	) => {
		const authUrl = unauthenticatedServices[option]?.outboundAuthUrl;
		if (!authUrl) {
			return;
		}
		const providerName = getProviderName(option);
		try {
			await auth(authUrl);
			onAuthSuccess?.(option);
			await onThirdPartyAuthRefetch();
			void flags?.showSuccessFlag({
				id: 'space-curation.third-party-auth.success',
				title: intl.formatMessage(i18n.successFlagTitle, { providerName }),
				description: intl.formatMessage(i18n.successFlagDescription),
				isAutoDismiss: true,
			});
		} catch (error) {
			if (error instanceof AuthError && error.type === 'auth_window_closed') {
				void flags?.showWarningFlag({
					id: 'space-curation.third-party-auth.cancelled',
					title: intl.formatMessage(i18n.cancelledFlagTitle),
					description: intl.formatMessage(i18n.cancelledFlagDescription, { providerName }),
					isAutoDismiss: true,
				});
			} else {
				void flags?.showErrorFlag({
					id: 'space-curation.third-party-auth.error',
					title: intl.formatMessage(i18n.errorFlagTitle),
					description: intl.formatMessage(i18n.errorFlagDescription, { providerName }),
					isAutoDismiss: true,
				});
			}
		}
	};

	const singleConnector = eligibleConnectors.length === 1 ? eligibleConnectors[0] : undefined;

	const CONNECTOR_UI: Record<
		(typeof AUTH_OPTIONS)[number],
		{
			singleIcon: 'google-drive' | 'onedrive';
			titleMessage: string;
		}
	> = {
		[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]: {
			singleIcon: 'google-drive',
			titleMessage: intl.formatMessage(i18n.titleGoogleDrive),
		},
		[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT]: {
			singleIcon: 'onedrive',
			titleMessage: intl.formatMessage(i18n.titleMicrosoft),
		},
	};

	const singleConnectorIcon = singleConnector
		? unauthenticatedServices[singleConnector]?.icon
		: undefined;
	const singleIcon = singleConnector ? CONNECTOR_UI[singleConnector].singleIcon : undefined;
	const titleText = singleConnector
		? CONNECTOR_UI[singleConnector].titleMessage
		: intl.formatMessage(i18n.titleMulti);
	const connectText = singleConnector
		? intl.formatMessage(i18n.connectProvider, { providerName: getProviderName(singleConnector) })
		: intl.formatMessage(i18n.connect);

	return (
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => (
				<>
					<Box xcss={styles.container} testId="space-navigation-third-party-auth">
						<Box xcss={styles.topRow}>
							<Pressable
								xcss={styles.mainAction}
								onClick={() => {
									if (spaceId) {
										createAnalyticsEvent({
											type: 'sendUIEvent',
											data: {
												action: 'clicked',
												actionSubject: 'curationEntry',
												source: 'containerNavigation',
												attributes: {
													spaceId,
													spaceKey,
													entryPoint: 'authEntry',
													eligibleConnectors,
												},
											},
										}).fire();
									}
									if (singleConnector) {
										void handleConnect(singleConnector, flags);
									} else {
										setIsModalOpen(true);
									}
								}}
							>
								<Box>
									<StackedContentIcons
										singleIcon={singleIcon}
										singleIconNode={
											singleConnectorIcon ? singleConnectorIcon({ size: 'small' }) : undefined
										}
									/>
									<Text color="color.text">
										{titleText} <InternalTestingLozenge />
									</Text>
								</Box>
								<Text color="color.link">{connectText}</Text>
							</Pressable>
							<CloseMenu
								onDismiss={() => void handleDismiss(flags)}
								onNeverShowAgain={() => void handleNeverShowAgain(flags)}
							/>
						</Box>
					</Box>
					{isModalOpen && (
						<ThirdPartyAuthenticationModal
							unauthenticatedServices={unauthenticatedServices}
							onClose={() => setIsModalOpen(false)}
							onConnect={(option) => void handleConnect(option, flags)}
						/>
					)}
				</>
			)}
		</Subscribe>
	);
};
